import { getVariant } from '@chessbut/engine';
import type { GameRecordSummary } from '@chessbut/shared';
import { LogIn, LogOut, Settings, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Avatar } from '../components/Brand.tsx';
import { api } from '../lib/api.ts';
import { Link } from '../lib/router.tsx';
import { logout, useSession } from '../lib/session.ts';
import { openAuth, openSettings } from '../lib/ui.ts';
import { GameList } from './ProfilePage.tsx';

export function YouPage() {
  const { me } = useSession();
  const [games, setGames] = useState<GameRecordSummary[] | null>(null);
  useEffect(() => {
    if (!me) return;
    api<{ games: GameRecordSummary[] }>('/api/me/games')
      .then((r) => setGames(r.games))
      .catch(() => setGames([]));
  }, [me?.id, me?.today.games]);
  if (!me) return <div className="page loading"><span className="spinner big" /></div>;
  const pools = Object.values(me.ratings).sort((a, b) => (a.pool === 'overall' ? -1 : b.pool === 'overall' ? 1 : b.games - a.games));
  const overall = me.ratings.overall;
  return (
    <div className="page">
      <section className="profile-head">
        <Avatar name={me.name} size={56} />
        <div className="grow">
          <h1 className="page-title">{me.guest ? me.name : me.username}</h1>
          <p className="muted small">
            {me.guest ? 'Playing as a guest on this device' : `Joined ${new Date(me.createdAt).toLocaleDateString()}`}
            {me.admin && (
              <>
                {' · '}
                <Link to="/admin">Dashboard</Link>
              </>
            )}
          </p>
        </div>
        <button className="btn ghost icon-only" aria-label="Settings" onClick={openSettings}>
          <Settings className="icon" />
        </button>
      </section>

      {me.guest && (
        <section className="account-cta">
          <h2 className="section-title">Your own name, on every device</h2>
          <p className="muted">
            Your games already save in this browser. A free account adds your name to the leaderboards and syncs your phone and laptop. No password
            needed.
          </p>
          <div className="row">
            <button className="btn primary" onClick={() => openAuth('signup')}>
              <UserPlus className="icon" /> Create account
            </button>
            <button className="btn ghost" onClick={() => openAuth('login')}>
              <LogIn className="icon" /> Sign in
            </button>
          </div>
        </section>
      )}

      <section className="stats">
        <div className="stat">
          <div className="k">Rating</div>
          <div className="v">
            {overall ? overall.rating : 1500}
            {(!overall || overall.provisional) && <span className="q">?</span>}
          </div>
        </div>
        <div className="stat">
          <div className="k">Today</div>
          <div className="v">
            {me.today.points}
            <small>pts</small>
          </div>
        </div>
        <div className="stat">
          <div className="k">Streak</div>
          <div className="v">
            {me.streak.current}
            <small>best {me.streak.best}</small>
          </div>
        </div>
      </section>

      {pools.length > 0 && (
        <section className="group">
          <h2 className="section-title">Ratings</h2>
          <div className="list">
            {pools.map((r) => (
              <div key={r.pool} className="list-row">
                <span className="grow">
                  <span className="name">{r.pool === 'overall' ? 'Overall' : (getVariant(r.pool)?.info.name ?? r.pool)}</span>
                  <span className="meta">
                    {r.games} games · {r.wins}W {r.losses}L {r.draws}D · best {r.best}
                  </span>
                </span>
                <span className="value">
                  {r.rating}
                  {r.provisional && <span className="q">?</span>}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="group">
        <h2 className="section-title">Recent games</h2>
        {games === null ? (
          <div className="center">
            <span className="spinner big" />
          </div>
        ) : games.length ? (
          <GameList games={games} playerId={me.id} />
        ) : (
          <p className="empty">No finished games yet.</p>
        )}
      </section>

      {!me.guest && (
        <div className="row-buttons">
          <Link to={`/u/${me.username}`} className="btn small">
            Public profile
          </Link>
          <button className="btn small ghost quiet" onClick={() => void logout()}>
            <LogOut className="icon sm" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}
